import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const PWA_DISMISSED_KEY = "hermansbar-pwa-install-dismissed";
const PWA_INSTALLED_KEY = "hermansbar-pwa-installed";

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function wasDismissed(): boolean {
  if (typeof localStorage === "undefined") return true;
  return localStorage.getItem(PWA_DISMISSED_KEY) === "true";
}

function wasInstalled(): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(PWA_INSTALLED_KEY) === "true";
}

export function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasDismissed() || wasInstalled()) return;

    const ua = navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    const handleAppInstalled = () => {
      localStorage.setItem(PWA_INSTALLED_KEY, "true");
      setVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    // For iOS, show the prompt after a short delay (no beforeinstallprompt event)
    if (isIOSDevice) {
      const timer = setTimeout(() => setVisible(true), 2000);
      return () => {
        window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        window.removeEventListener("appinstalled", handleAppInstalled);
        clearTimeout(timer);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        localStorage.setItem(PWA_INSTALLED_KEY, "true");
      }
      setDeferredPrompt(null);
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(PWA_DISMISSED_KEY, "true");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-[100] animate-slide-up"
      role="dialog"
      aria-label="Install Hermansbar app"
    >
      <div className="bg-bar-card border border-white/10 rounded-2xl shadow-bar-lg overflow-hidden">
        <div className="p-4 flex gap-3">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-bar-accent/20 flex items-center justify-center">
            <Download className="w-6 h-6 text-bar-accent" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-bar-cream">Install Hermansbar</h3>
            <p className="text-sm text-bar-muted mt-0.5">
              {isIOS
                ? "Add to your home screen for quick access and a full-screen experience."
                : "Install our app for quick access, offline support, and push notifications."}
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1.5 rounded-lg text-bar-muted hover:text-bar-cream hover:bg-white/10 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-4 pb-4 flex gap-2">
          {isIOS ? (
            <>
              <p className="text-xs text-bar-muted flex-1">
                Tap the share button, then &quot;Add to Home Screen&quot;
              </p>
              <button
                onClick={handleDismiss}
                className="px-4 py-2.5 rounded-xl text-bar-muted hover:text-bar-cream hover:bg-white/5 transition-colors flex-shrink-0"
              >
                Got it
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleInstall}
                className="flex-1 px-4 py-2.5 rounded-xl bg-bar-accent text-bar-dark font-semibold hover:bg-bar-accent-hover transition-colors flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Install
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2.5 rounded-xl text-bar-muted hover:text-bar-cream hover:bg-white/5 transition-colors"
              >
                Not now
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
